import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import {
  ShippingPolicy,
  daysToOfferDuration,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';
import { areAllShippingFeesZero } from '@/lib/utils/shipping-policy-utils';
import { productsState } from '@/states/products.state';
import { shippingPolicyState } from '@/states/shipping-policy.state';
import { useMutation } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';

import { dealAtom, dealRefetchAtom } from '../../states/deal.state';
import {
  DealShippingPolicy,
  DealShippingPolicyInput,
  EditDealShippingPolicyDocument,
} from '../graphql/generated/graphql';
import { useDealNotEditableHandler } from './useDealNotEditableHandler';

const toUIFormat = (apiData: DealShippingPolicy): ShippingPolicy => {
  return {
    freeShippingMinimumOrderAmount:
      apiData.freeShippingMinimumOrderAmount ?? null,
    deliveryOfferedNumberOfDays: apiData.deliveryOfferedNumberOfDays ?? null,
    pickupPointNetwork: apiData.pickupPointNetwork ?? null,
    basicFrPickupPointDeliveryPrice:
      apiData.basicFrPickupPointDeliveryPrice ?? null,
    minimumOrderEnabled: apiData.freeShippingMinimumOrderAmount !== null,
    minimumOrderAmount: apiData.freeShippingMinimumOrderAmount
      ? apiData.freeShippingMinimumOrderAmount / 100
      : null,
    timeLimitedEnabled: apiData.deliveryOfferedNumberOfDays !== null,
    offerDuration: daysToOfferDuration(
      apiData.deliveryOfferedNumberOfDays ?? null
    ),
    pickupPointEnabled: apiData.pickupPointNetwork !== null,
    pickupPointDeliveryPrice:
      typeof apiData.basicFrPickupPointDeliveryPrice === 'number'
        ? apiData.basicFrPickupPointDeliveryPrice / 100
        : null,
    allShippingFeesZero: false,
  };
};

const toAPIFormat = (uiData: ShippingPolicy): DealShippingPolicyInput => {
  return {
    freeShippingMinimumOrderAmount: uiData.minimumOrderEnabled
      ? uiData.minimumOrderAmount
        ? Math.round(uiData.minimumOrderAmount * 100)
        : null
      : null,
    deliveryOfferedNumberOfDays: uiData.timeLimitedEnabled
      ? offerDurationToDays(uiData.offerDuration)
      : null,
    pickupPointNetwork: uiData.pickupPointEnabled
      ? uiData.pickupPointNetwork
      : null,
    basicFrPickupPointDeliveryPrice: uiData.pickupPointEnabled
      ? uiData.pickupPointDeliveryPrice !== null
        ? Math.round(uiData.pickupPointDeliveryPrice * 100)
        : null
      : null,
  };
};

export function useShippingPolicy(dealId: string) {
  const [shippingPolicy, setShippingPolicy] = useAtom(shippingPolicyState);
  const dealItems = useAtomValue(productsState);
  const isOfferLocked = useIsOfferLocked();
  const { t } = useTranslation();
  const { toast } = useToast();
  const hasAutoSyncedZeroFeesRef = useRef(false);
  const isAutoSyncInFlightRef = useRef(false);
  const lastCommittedPolicyRef = useRef<ShippingPolicy>(shippingPolicy);

  const deal = useAtomValue(dealAtom);
  const dealRefetch = useAtomValue(dealRefetchAtom);
  const shippingPolicyGraphQL = deal?.dealShippingPolicy;

  // Calculate allShippingFeesZero with useMemo
  const allShippingFeesZero = useMemo(
    () => areAllShippingFeesZero(dealItems),
    [dealItems]
  );

  useEffect(() => {
    setShippingPolicy((prev) => {
      if (shippingPolicyGraphQL) {
        return {
          ...toUIFormat(shippingPolicyGraphQL),
          allShippingFeesZero,
        };
      }

      if (prev.allShippingFeesZero === allShippingFeesZero) {
        return prev;
      }

      return {
        ...prev,
        allShippingFeesZero,
      };
    });
  }, [shippingPolicyGraphQL, allShippingFeesZero, setShippingPolicy]);

  const resolvedPolicy = useMemo(() => {
    if (shippingPolicyGraphQL) {
      return {
        ...toUIFormat(shippingPolicyGraphQL),
        allShippingFeesZero,
      };
    }
    return shippingPolicy;
  }, [shippingPolicyGraphQL, allShippingFeesZero, shippingPolicy]);

  useEffect(() => {
    lastCommittedPolicyRef.current = resolvedPolicy;
  }, [resolvedPolicy]);

  const [editDealShippingPolicy] = useMutation(EditDealShippingPolicyDocument);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  const handleShippingPolicyChange = useCallback(
    async (newPolicy: ShippingPolicy): Promise<boolean> => {
      if (isOfferLocked) {
        return false;
      }

      const previousPolicy = lastCommittedPolicyRef.current;
      setShippingPolicy(newPolicy);

      try {
        const result = await editDealShippingPolicy({
          variables: {
            dealId: dealId,
            shippingPolicy: toAPIFormat(newPolicy),
          },
        });

        const payload = result.data?.editDealShippingPolicy;
        if (payload?.__typename === 'DomainException') {
          if (handleIfDealNotEditable(payload.code)) {
            setShippingPolicy(previousPolicy);
            return false;
          }
          setShippingPolicy(previousPolicy);
          return false;
        }

        // Explicitly refetch deal to sync dealAtom with the new shipping policy.
        // Catch to avoid triggering the outer catch on a refetch network failure.
        await dealRefetch.fn().catch(() => {});
        return true;
      } catch {
        // Revert optimistic UI update on transport/runtime failures.
        setShippingPolicy(previousPolicy);
        toast({
          title: t('general.error'),
          description: t('general.unexpected_error'),
          variant: 'destructive',
        });
        return false;
      }
    },
    [
      dealId,
      dealRefetch,
      editDealShippingPolicy,
      handleIfDealNotEditable,
      isOfferLocked,
      setShippingPolicy,
      t,
      toast,
    ]
  );

  useEffect(() => {
    if (!allShippingFeesZero) {
      hasAutoSyncedZeroFeesRef.current = false;
      isAutoSyncInFlightRef.current = false;
      return;
    }

    if (
      isOfferLocked ||
      hasAutoSyncedZeroFeesRef.current ||
      isAutoSyncInFlightRef.current
    ) {
      return;
    }

    const policySource = shippingPolicyGraphQL
      ? toUIFormat(shippingPolicyGraphQL)
      : shippingPolicy;

    const hasFreeShippingConditions =
      policySource.minimumOrderEnabled || policySource.timeLimitedEnabled;

    if (!hasFreeShippingConditions) {
      hasAutoSyncedZeroFeesRef.current = true;
      return;
    }

    const disabledFreeShippingPolicy: ShippingPolicy = {
      ...policySource,
      allShippingFeesZero,
      minimumOrderEnabled: false,
      timeLimitedEnabled: false,
      minimumOrderAmount: null,
      offerDuration: null,
    };

    isAutoSyncInFlightRef.current = true;
    void (async () => {
      try {
        const didSync = await handleShippingPolicyChange(
          disabledFreeShippingPolicy
        );
        hasAutoSyncedZeroFeesRef.current = didSync;
      } finally {
        isAutoSyncInFlightRef.current = false;
      }
    })();
  }, [
    allShippingFeesZero,
    handleShippingPolicyChange,
    isOfferLocked,
    shippingPolicy,
    shippingPolicyGraphQL,
  ]);

  return {
    shippingPolicy: resolvedPolicy,
    handleShippingPolicyChange,
  };
}
